import type { CountryOption, TimezoneOption, CurrencyOption } from "../types";

let countryDataListPromise: Promise<typeof import("country-data-list")> | null = null;

const getCountryDataList = () => {
  if (!countryDataListPromise) {
    countryDataListPromise = import("country-data-list");
  }
  return countryDataListPromise;
};

const languageLocalePrefixes: Record<string, string> = {
  ara: "ar",
  deu: "de",
  eng: "en",
  fra: "fr",
  hin: "hi",
  ita: "it",
  jpn: "ja",
  por: "pt",
  spa: "es",
  zho: "zh",
};

const toLocale = (countryCode: string, languages: string[] = []) => {
  const language = languages.map((code) => languageLocalePrefixes[code]).find(Boolean) || "en";
  return `${language}_${countryCode}`;
};

/**
 * Dynamically load country data to avoid increasing bundle size
 */
export async function loadCountries(): Promise<CountryOption[]> {
  try {
    const { countries, timezones } = await getCountryDataList();

    return countries.all
      .filter((c: any) => c.status === "assigned")
      .map((country: any) => ({
        value: country.alpha2,
        label: country.name,
        flag: country.emoji,
        currency: country.currencies?.find((currency: string) => currency.length === 3),
        timezone: timezones.getTimezonesByCountry?.(country.alpha2)?.[0],
        locale: toLocale(country.alpha2, country.languages),
      }))
      .sort((a: CountryOption, b: CountryOption) => a.label.localeCompare(b.label));
  } catch (error) {
    console.error("Failed to load countries:", error);
    return [];
  }
}

/**
 * Dynamically load timezones to avoid increasing bundle size
 */
export async function loadTimezones(): Promise<TimezoneOption[]> {
  const fallback = [
    { value: "Europe/Zurich", label: "Europe/Zurich", offset: "GMT+1" },
    { value: "Europe/London", label: "Europe/London", offset: "GMT+0" },
    { value: "America/New_York", label: "America/New York", offset: "GMT-5" },
  ];

  try {
    const { timezones } = await getCountryDataList();

    return timezones.all.map((tz: string) => {
      const offset = timezones.getUtcOffset?.(tz);
      const formattedOffset = offset ? `GMT${offset}` : "GMT";
      return {
        value: tz,
        label: tz,
        offset: formattedOffset,
      } as TimezoneOption;
    });
  } catch (error) {
    console.error("Failed to load timezones:", error);
    return fallback;
  }
}

/**
 * Dynamically load currencies to avoid increasing bundle size
 */
export async function loadCurrencies(): Promise<CurrencyOption[]> {
  try {
    const { currencies } = await getCountryDataList();

    return currencies.all
      .map((curr: any) => ({
        value: curr.code,
        label: curr.name,
        symbol: curr.symbol || curr.code,
      }))
      .sort((a: CurrencyOption, b: CurrencyOption) => a.label.localeCompare(b.label));
  } catch (error) {
    console.error("Failed to load currencies:", error);
    return [];
  }
}

/**
 * Common locales
 */
export function getLocales(): Array<{ value: string; label: string }> {
  return [
    { value: "en_US", label: "English (United States)" },
    { value: "en_GB", label: "English (United Kingdom)" },
    { value: "fr_FR", label: "Français (France)" },
    { value: "fr_CH", label: "Français (Suisse)" },
    { value: "de_DE", label: "Deutsch (Deutschland)" },
    { value: "de_CH", label: "Deutsch (Schweiz)" },
    { value: "it_IT", label: "Italiano (Italia)" },
    { value: "it_CH", label: "Italiano (Svizzera)" },
    { value: "es_ES", label: "Español (España)" },
    { value: "pt_BR", label: "Português (Brasil)" },
    { value: "ja_JP", label: "日本語 (日本)" },
    { value: "zh_CN", label: "中文 (中国)" },
  ];
}
